/**
 * 染色体級（chr1 ≈249 Mbp）を扱うためのチャンク配列（§0.1「チャンク格納」「全文 string 化禁止」）。
 *
 * - 塩基は 1 byte コード（`nucleotideCodes`）で固定長チャンクに入れる
 * - 実体は `ChunkSource`（メモリ・IndexedDB・ZIP など）に委ね、ここは LRU 常駐と区間読みだけ持つ
 * - 描画は同期に読みたいので `readSync`（常駐していなければ `null`）と `ensure`（非同期先読み）を分ける
 */

import { DEFAULT_CHUNK_SIZE, DEFAULT_RESIDENT_BYTES_LIMIT, MAX_SEQUENCE_LENGTH } from '../constants';
import { countGc, decodeCodes } from './nucleotideCodes';
import { Interval, Topology, intervalLength, resolveSpans, spansLength } from './interval';

export interface ChunkSource {
  readonly chunkSize: number;
  readonly chunkCount: number;
  readChunk(index: number): Promise<Uint8Array>;
  writeChunk?(index: number, codes: Uint8Array): Promise<void>;
  /** 既に手元にあるなら同期で返す（ZIP 展開直後・小さい配列など） */
  peekChunk?(index: number): Uint8Array | undefined;
  dispose?(): Promise<void>;
}

export function chunkCountFor(length: number, chunkSize: number): number {
  return length <= 0 ? 0 : Math.ceil(length / chunkSize);
}

/** メモリ常駐の `ChunkSource`。小さい配列・テスト・取込途中の受け皿に使う。 */
export class MemoryChunkSource implements ChunkSource {
  readonly chunkSize: number;
  private readonly chunks: Uint8Array[];

  constructor(chunks: Uint8Array[], chunkSize: number = DEFAULT_CHUNK_SIZE) {
    this.chunkSize = chunkSize;
    this.chunks = chunks;
  }

  /** 連続した塩基コード列を分割して作る */
  static fromCodes(codes: Uint8Array, chunkSize: number = DEFAULT_CHUNK_SIZE): MemoryChunkSource {
    const chunks: Uint8Array[] = [];
    for (let offset = 0; offset < codes.length; offset += chunkSize) {
      chunks.push(codes.slice(offset, Math.min(offset + chunkSize, codes.length)));
    }
    return new MemoryChunkSource(chunks, chunkSize);
  }

  get chunkCount(): number {
    return this.chunks.length;
  }

  peekChunk(index: number): Uint8Array | undefined {
    return this.chunks[index];
  }

  async readChunk(index: number): Promise<Uint8Array> {
    const chunk = this.chunks[index];
    if (!chunk) throw new Error(`チャンクがありません: ${index}`);
    return chunk;
  }

  async writeChunk(index: number, codes: Uint8Array): Promise<void> {
    this.chunks[index] = codes;
  }
}

export interface ChunkedSequenceInit {
  id: string;
  name: string;
  description?: string;
  length: number;
  topology: Topology;
  source: ChunkSource;
  /** LRU で常駐させる上限（バイト）。既定 64 MB */
  residentBytesLimit?: number;
}

export class ChunkedSequence {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly length: number;
  readonly topology: Topology;
  readonly chunkSize: number;
  readonly chunkCount: number;

  private readonly source: ChunkSource;
  private readonly residentBytesLimit: number;
  /** LRU。Map の挿入順を使い、参照のたび末尾へ移す */
  private readonly resident = new Map<number, Uint8Array>();
  private readonly pending = new Map<number, Promise<Uint8Array>>();
  private residentBytes = 0;

  constructor(init: ChunkedSequenceInit) {
    if (init.length < 0 || init.length > MAX_SEQUENCE_LENGTH) {
      throw new Error(
        `配列長が上限を超えています: ${init.length} bp（上限 ${MAX_SEQUENCE_LENGTH} bp）`
      );
    }
    this.id = init.id;
    this.name = init.name;
    this.description = init.description ?? '';
    this.length = init.length;
    this.topology = init.topology;
    this.source = init.source;
    this.chunkSize = init.source.chunkSize;
    this.chunkCount = chunkCountFor(init.length, this.chunkSize);
    this.residentBytesLimit = init.residentBytesLimit ?? DEFAULT_RESIDENT_BYTES_LIMIT;
  }

  get chunkSource(): ChunkSource {
    return this.source;
  }

  chunkIndexAt(position: number): number {
    return Math.floor(position / this.chunkSize);
  }

  /** 区間を実体スパンへ（原点ラップ込み） */
  spansOf(interval: Interval): Interval[] {
    return resolveSpans(interval, this.length, this.topology);
  }

  /** 区間が触れるチャンク番号 */
  chunkIndicesFor(interval: Interval): number[] {
    const indices: number[] = [];
    for (const span of this.spansOf(interval)) {
      const first = this.chunkIndexAt(span.start);
      const last = this.chunkIndexAt(Math.max(span.start, span.end - 1));
      for (let index = first; index <= last; index += 1) {
        if (!indices.includes(index)) indices.push(index);
      }
    }
    return indices;
  }

  isResident(interval: Interval): boolean {
    return this.chunkIndicesFor(interval).every((index) => this.resident.has(index));
  }

  /** 可視範囲＋余裕を先読みする。`padding` は前後に足す塩基数。 */
  async ensure(interval: Interval, padding = 0): Promise<void> {
    const target =
      padding > 0
        ? { start: interval.start - padding, end: interval.end + padding }
        : interval;
    const indices = this.chunkIndicesFor(target);
    await Promise.all(indices.map((index) => this.loadChunk(index)));
    this.evict(new Set(indices));
  }

  /** 常駐していれば同期で返す。描画ループはこれを使う。 */
  readSync(interval: Interval): Uint8Array | null {
    const spans = this.spansOf(interval);
    const total = spansLength(spans);
    if (total === 0) return new Uint8Array(0);

    const out = new Uint8Array(total);
    let offset = 0;
    for (const span of spans) {
      for (let position = span.start; position < span.end; ) {
        const chunkIndex = this.chunkIndexAt(position);
        const chunk = this.touch(chunkIndex);
        if (!chunk) return null;
        const chunkStart = chunkIndex * this.chunkSize;
        const from = position - chunkStart;
        const take = Math.min(chunk.length - from, span.end - position);
        if (take <= 0) return null;
        out.set(chunk.subarray(from, from + take), offset);
        offset += take;
        position += take;
      }
    }
    return offset === total ? out : out.subarray(0, offset);
  }

  /** 非同期読み。必要なチャンクを取り寄せてから返す。 */
  async read(interval: Interval): Promise<Uint8Array> {
    const spans = this.spansOf(interval);
    const total = spansLength(spans);
    // 常駐上限を超える連続バッファは作らない（chr1 全長の誤呼び出しを防ぐ）
    if (total > this.residentBytesLimit) {
      throw new Error(
        `読み取り区間が常駐上限を超えています: ${total.toLocaleString()} bp（上限 ${this.residentBytesLimit.toLocaleString()} バイト）`
      );
    }
    await this.ensure(interval);
    const codes = this.readSync(interval);
    if (!codes) throw new Error('チャンクの読み込みに失敗しました');
    return codes;
  }

  async readText(interval: Interval): Promise<string> {
    return decodeCodes(await this.read(interval));
  }

  /** GC%。N を含む区間でも分母は区間長にする（旧実装と同じ）。長大区間はチャンク単位で積算する。 */
  async gcPercent(interval: Interval): Promise<number | null> {
    const spans = this.spansOf(interval);
    const total = spansLength(spans);
    if (total === 0) return null;
    if (total <= this.residentBytesLimit) {
      const codes = await this.read(interval);
      return (countGc(codes) / codes.length) * 100;
    }
    let gc = 0;
    for (const span of spans) {
      for (let start = span.start; start < span.end; ) {
        const end = Math.min(span.end, start + this.chunkSize);
        const slice = await this.read({ start, end });
        gc += countGc(slice);
        start = end;
      }
    }
    return (gc / total) * 100;
  }

  /** 常駐しているチャンクだけで GC% を出す（描画中の即時表示用） */
  gcPercentSync(interval: Interval): number | null {
    const codes = this.readSync(interval);
    if (!codes || codes.length === 0) return null;
    return (countGc(codes) / codes.length) * 100;
  }

  get residentByteCount(): number {
    return this.residentBytes;
  }

  clearCache(): void {
    this.resident.clear();
    this.residentBytes = 0;
  }

  private touch(index: number): Uint8Array | undefined {
    const chunk = this.resident.get(index);
    if (chunk) {
      this.resident.delete(index);
      this.resident.set(index, chunk);
      return chunk;
    }
    const peeked = this.source.peekChunk?.(index);
    if (peeked) {
      this.admit(index, peeked);
      // peek 経路でも LRU を守る（ensure を通らない readSync 用）
      this.evict(new Set([index]));
      return peeked;
    }
    return undefined;
  }

  private admit(index: number, chunk: Uint8Array): void {
    if (this.resident.has(index)) return;
    this.resident.set(index, chunk);
    this.residentBytes += chunk.length;
  }

  private async loadChunk(index: number): Promise<Uint8Array> {
    const existing = this.touch(index);
    if (existing) return existing;

    const inflight = this.pending.get(index);
    if (inflight) return inflight;

    const promise = this.source
      .readChunk(index)
      .then((chunk) => {
        this.admit(index, chunk);
        this.pending.delete(index);
        return chunk;
      })
      .catch((error) => {
        this.pending.delete(index);
        throw error;
      });
    this.pending.set(index, promise);
    return promise;
  }

  /** LRU 追い出し。`keep` に入っているチャンクは残す。 */
  private evict(keep: Set<number>): void {
    if (this.residentBytes <= this.residentBytesLimit) return;
    for (const [index, chunk] of this.resident) {
      if (this.residentBytes <= this.residentBytesLimit) break;
      if (keep.has(index)) continue;
      this.resident.delete(index);
      this.residentBytes -= chunk.length;
    }
  }
}

/** 小さい配列（デモ・貼り付け）をそのまま `ChunkedSequence` にする補助 */
export function chunkedSequenceFromCodes(params: {
  id: string;
  name: string;
  description?: string;
  codes: Uint8Array;
  topology: Topology;
  chunkSize?: number;
}): ChunkedSequence {
  const chunkSize = params.chunkSize ?? DEFAULT_CHUNK_SIZE;
  return new ChunkedSequence({
    id: params.id,
    name: params.name,
    description: params.description,
    length: params.codes.length,
    topology: params.topology,
    source: MemoryChunkSource.fromCodes(params.codes, chunkSize),
  });
}

export { intervalLength };
